export const SITE_NAME = "U.CENTER Explained";
export const SITE_DOMAIN = "https://ucenter.guide";
export const LAST_REVIEWED = "2026-09-07";
export const LAST_REVIEWED_LABEL = "7 September 2026";
export const INDEPENDENT_NOTICE =
  "An independent review by an anonymous researcher. Not the official U.CENTER or U-TOPIA website.";

export const RISK_POINTS = [
  "Membership is a purchase, not a job.",
  "Ranks and rewards are not guaranteed.",
  "No ROI and no dividends are promised, proffered, or offered.",
  "Crypto and fintech apps carry risk.",
  "Only spend what you can afford to lose.",
  "Read the live terms on u.center and u-topia.com before paying.",
];

export const CANONICAL_SOURCES = [
  { label: "u.center, the official U.CENTER platform", href: "https://u.center" },
  { label: "u-topia.com, the official U-TOPIA site", href: "https://u-topia.com" },
  { label: "U.CENTER official compensation one-pager (mirrored PDF, September 2026)", href: "/docs/u-center-compensation-plan-one-pager.pdf" },
  { label: "U.CENTER Academy videos (inside the platform)", href: "https://u.center" },
  { label: "U.CENTER YouTube channel", href: "https://u.center" },
];

export const DILIGENCE_CHECKLIST = [
  {
    title: "Read the live terms yourself",
    body: "I started here, and you should too. Terms change, so read the current ones on u.center and u-topia.com, not a screenshot someone sent you.",
  },
  {
    title: "Confirm the DAO registration",
    body: "Done for you. The certificate of incorporation is published in this review: Union Center DAO Incorporated, reg no. 03010106, incorporated as a DAO Association on 14 July 2026. The document carries a QR code; scan it to verify authenticity with the registrar directly.",
  },
  {
    title: "Read the official compensation one-pager",
    body: "Every percentage in this review comes from that document. Check my work against the original, not against a chat message.",
  },
  {
    title: "Understand QV before you pay",
    body: "Qualifying Volume, the 50% rule, two legs, the downline rank rule. Learn them until you can explain them back. It took me an evening; it's worth it.",
  },
  {
    title: "Get any income claim in writing",
    body: "The company's own position is that no ROI and no dividends are promised. If anyone tells you otherwise, ask them where that's written. That question ends most bad pitches.",
  },
  {
    title: "Check whether the app supports your country",
    body: "U-TOPIA is in beta until the 16 October public launch, and country availability varies. Confirm yours before planning around it.",
  },
  {
    title: "Accept that points economics aren't public yet",
    body: "Supply and distribution are coming in a white paper that isn't out yet. Until it is, that part is genuinely unverifiable, and I flag it every time it matters.",
  },
  {
    title: "Only spend what you can afford to lose",
    body: "The oldest rule, and still the right one. Membership is a purchase. Treat the money as spent, and any upside as upside.",
  },
];

export const COMPARISON_ROWS = [
  ["Legal form", "Registered DAO Association (Union Center DAO Incorporated, reg 03010106)", "Private company"],
  ["Jurisdiction", "Ras Al Khaimah, UAE (Innovation City)", "United States"],
  ["Purpose", "Education, marketing, community, ambassador layer", "Fintech super app: wallet, swaps, Visa tap-to-pay"],
  ["Status", "Fully live; launched May 2026", "Beta live; public launch 16 October, Flow Summit, Bangkok"],
  ["Relationship", "Aligned ecosystem partner; facilitates the U-TOPIA launch", "Builds the product U.CENTER's community gathers around"],
];

export const PACKAGES = [
  ["Starter", "$25", "–", "Community platform access (lifetime), U Academy (1 month)"],
  ["Bronze", "$100", "77.00", "U Academy (4 months) plus pre-sale bonus (1 month)"],
  ["Silver", "$250", "202.13", "U Academy (12 months) plus pre-sale bonus (3 months)"],
  ["Gold", "$500", "415.80", "U Career (12 months), U Academy (24), 3 referrals; marked “most popular” on u.center"],
  ["Platinum", "$1,000", "870.10", "U Career (24 months), U Academy (36), 5 referrals, U-AI (12), unlimited access"],
  ["Diamond", "$2,500", "2213.75", "U Career (lifetime), U Academy (60), 10 referrals, U-AI (24), VIP access"],
  ["Full House", "All packages", "–", "Every package combined"],
];

export const QV_TABLE = [
  ["F1", "$0"], ["F2", "$1,000"], ["F3", "$2,000"], ["F4", "$4,000"], ["F5", "$8,000"],
  ["F6", "$16,000"], ["F7", "$32,000"], ["F8", "$64,000"], ["F9", "$128,000"], ["F10", "$256,000"],
  ["F11", "$512,000"], ["F12", "$1M"], ["F13", "$2M"], ["F14", "$4M"], ["F15", "$8M"],
  ["F16", "$16M"], ["F17", "$32M"], ["F18", "$64M"], ["F19", "$128M"], ["F20", "$256M"],
];

export const NAV_SECTIONS = [
  {
    label: "The Review",
    links: [
      { to: "/what-is-ucenter", label: "What is U.CENTER?" },
      { to: "/what-is-utopia", label: "What is U-TOPIA?" },
      { to: "/ucenter-vs-utopia", label: "U.CENTER vs U-TOPIA" },
      { to: "/leadership", label: "Leadership" },
      { to: "/emmanuel-quezada", label: "Emmanuel Quezada" },
      { to: "/timeline", label: "Timeline" },
    ],
  },
  {
    label: "The Money",
    links: [
      { to: "/compensation-plan", label: "Compensation plan" },
      { to: "/how-ranks-work", label: "How ranks work" },
      { to: "/points-and-ownership", label: "Points & ownership" },
      { to: "/bonuses", label: "The four bonuses" },
    ],
  },
  {
    label: "The Product",
    links: [
      { to: "/utopia-app", label: "The U-TOPIA app" },
      { to: "/membership-packages", label: "Membership packages" },
      { to: "/dao-governance", label: "DAO governance" },
      { to: "/academy", label: "Academy" },
    ],
  },
  {
    label: "My Method",
    links: [
      { to: "/how-to-verify", label: "How to verify" },
      { to: "/risk", label: "Risk & disclaimers" },
      { to: "/sources", label: "Sources" },
      { to: "/glossary", label: "Glossary" },
      { to: "/contact", label: "Correct my work" },
    ],
  },
];

export const FAQ_LIST = [
  { slug: "is-ucenter-a-bank", q: "Is U.CENTER a bank?" },
  { slug: "is-ucenter-an-mlm", q: "Is U.CENTER an MLM?" },
  { slug: "is-there-a-product-besides-membership", q: "Is there a product besides membership?" },
  { slug: "who-owns-ucenter", q: "Who owns U.CENTER?" },
  { slug: "why-is-ucenter-newer-than-utopia", q: "Why is U.CENTER newer than U-TOPIA?" },
  { slug: "can-i-use-utopia-without-joining", q: "Can I use U-TOPIA without joining?" },
  { slug: "where-does-the-team-operate", q: "Where does the team operate?" },
  { slug: "what-should-i-read-before-paying", q: "What should I read before paying?" },
  { slug: "are-points-a-security", q: "Are points a security?" },
  { slug: "is-income-guaranteed", q: "Is income guaranteed?" },
];
